import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import * as MUI from '@mui/material';
// Import the MUI preload utility
import { preloadMuiComponents } from './utils/muiPreload';
// Import the firebase utility instead of direct Firebase imports
import './utils/firebase';

// Theme
import theme from './styles/theme';

// Context Providers
import { BookmarkProvider } from './contexts/BookmarkContext';

// Layout components
import Layout from './components/layout/Layout';
import ContentLoader from './components/shared/ContentLoader';
import CookieConsent from './components/shared/CookieConsent';

// Directly import ScientificCalculator to avoid chunk loading issues
import ScientificCalculator from './components/calculators/ScientificCalculator';
import ComingSoonCalculatorWrapper from './components/calculators/ComingSoonCalculatorWrapper';

// Utility for lazy loading
import lazyLoad from './utils/lazyLoad';
// Specialized calculator lazy loading with data prefetching
import { lazyCalculatorComponent } from './utils/calculatorLoader';

// Lazy load pages for better performance
const HomePage = lazyLoad(() => import('./pages/home/HomePage'), {
  loadingMessage: 'Loading homepage...'
});
const NotFoundPage = lazyLoad(() => import('./pages/error/NotFoundPage'), {
  loadingMessage: 'Loading page...'
});
const AboutPage = lazyLoad(() => import('./pages/about/AboutPage'), {
  loadingMessage: 'Loading about page...'
});

// Lazy load blog pages - Using React.lazy and Suspense directly
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));

const ScientificCalculatorPage = () => (
  <Suspense fallback={<ContentLoader message="Loading Scientific Calculator..." />}>
    <ScientificCalculator />
  </Suspense>
);

// Calculator Pages - Using enhanced lazy loading with data prefetching
const PercentageCalculatorPage = lazyCalculatorComponent('Percentage');
const InvestmentCalculatorPage = lazyCalculatorComponent('Investment');
const RetirementCalculatorPage = lazyCalculatorComponent('Retirement');
// const ScientificCalculatorPage = lazyCalculatorComponent('Scientific'); // Commented out lazy loading
const BMICalculatorPage = lazyCalculatorComponent('BMI');
const UnitConversionCalculatorPage = lazyCalculatorComponent('UnitConversion');
const CalorieCalculatorPage = lazyCalculatorComponent('Calorie');
const MortgageCalculatorPage = lazyCalculatorComponent('Mortgage');
const AmortizationCalculatorPage = lazyCalculatorComponent('Amortization');
const InterestCalculatorPage = lazyCalculatorComponent('Interest');
const TaxCalculatorPage = lazyCalculatorComponent('Tax');
const AutoLoanCalculatorPage = lazyCalculatorComponent('AutoLoan');

// Coming Soon Calculator Pages - Using wrapper component
const DiscountCalculatorPage = () => <ComingSoonCalculatorWrapper calculatorType="Discount" />;
const TipCalculatorPage = () => <ComingSoonCalculatorWrapper calculatorType="Tip" />;
const StatisticsCalculatorPage = () => <ComingSoonCalculatorWrapper calculatorType="Statistics" />;
const GeometryCalculatorPage = () => <ComingSoonCalculatorWrapper calculatorType="Geometry" />;
const FractionCalculatorPage = () => <ComingSoonCalculatorWrapper calculatorType="Fraction" />;
const BodyFatCalculatorPage = lazyCalculatorComponent('BodyFat');
const MacroCalculatorPage = () => <ComingSoonCalculatorWrapper calculatorType="Macro" />;
const IdealWeightCalculatorPage = lazyCalculatorComponent('IdealWeight');

// Category Pages - Lazy loaded
const FinancialCalculatorsPage = lazyLoad(() => import('./pages/categories/FinancialCalculatorsPage'));
const MathCalculatorsPage = lazyLoad(() => import('./pages/categories/MathCalculatorsPage'));
const FitnessCalculatorsPage = lazyLoad(() => import('./pages/categories/FitnessCalculatorsPage'));
const OtherCalculatorsPage = lazyLoad(() => import('./pages/categories/OtherCalculatorsPage'));

// Legal Pages - Lazy loaded
const PrivacyPolicyPage = lazyLoad(() => import('./pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazyLoad(() => import('./pages/legal/TermsOfServicePage'));
const CookiePolicyPage = lazyLoad(() => import('./pages/legal/CookiePolicyPage'));
const SitemapPage = lazyLoad(() => import('./pages/legal/SitemapPage'));

function App() {
  useEffect(() => {
    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js').then(registration => {
          console.log('SW registered: ', registration);
        }).catch(registrationError => {
          console.log('SW registration failed: ', registrationError);
        });
      });
    }
    // Preload MUI components
    preloadMuiComponents();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <MUI.CssBaseline />
      <BookmarkProvider>
        {/* Cookie Consent Banner */}
        <CookieConsent />
        
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            
            {/* About Page */}
            <Route path="about" element={<AboutPage />} />
            
            {/* Blog Routes */}
            <Route 
              path="blog" 
              element={
                <Suspense fallback={<ContentLoader loading={true} message="Loading blog..." minHeight={300} />}>
                  <BlogPage />
                </Suspense>
              } 
            />
            <Route 
              path="blog/:slug" 
              element={
                <Suspense fallback={<ContentLoader loading={true} message="Loading article..." minHeight={400} />}>
                  <BlogPostPage />
                </Suspense>
              } 
            />
            
            {/* Legal Pages */}
            <Route path="privacy" element={<PrivacyPolicyPage />} />
            <Route path="terms" element={<TermsOfServicePage />} />
            <Route path="cookies" element={<CookiePolicyPage />} />
            <Route path="sitemap" element={<SitemapPage />} />
            
            {/* Category Routes */}
            <Route path="financial" element={<FinancialCalculatorsPage />} />
            <Route path="math" element={<MathCalculatorsPage />} />
            <Route path="fitness" element={<FitnessCalculatorsPage />} />
            <Route path="other" element={<OtherCalculatorsPage />} />
            
            {/* Calculator Routes - SEO-friendly URL structure */}
            {/* Financial Calculators */}
            <Route path="financial/mortgage-calculator" element={<MortgageCalculatorPage />} />
            <Route path="financial/investment-calculator" element={<InvestmentCalculatorPage />} />
            <Route path="financial/retirement-calculator" element={<RetirementCalculatorPage />} />
            <Route path="financial/amortization-calculator" element={<AmortizationCalculatorPage />} />
            <Route path="financial/interest-calculator" element={<InterestCalculatorPage />} />
            <Route path="financial/tax-calculator" element={<TaxCalculatorPage />} />
            <Route path="financial/auto-loan-calculator" element={<AutoLoanCalculatorPage />} />
            <Route path="financial/discount-calculator" element={<DiscountCalculatorPage />} />
            <Route path="financial/tip-calculator" element={<TipCalculatorPage />} />
            
            {/* Math Calculators */}
            <Route path="math/scientific-calculator" element={<ScientificCalculatorPage />} />
            <Route path="math/percentage-calculator" element={<PercentageCalculatorPage />} />
            <Route path="math/unit-conversion-calculator" element={<UnitConversionCalculatorPage />} />
            <Route path="math/statistics-calculator" element={<StatisticsCalculatorPage />} />
            <Route path="math/geometry-calculator" element={<GeometryCalculatorPage />} />
            <Route path="math/fraction-calculator" element={<FractionCalculatorPage />} />
            
            {/* Fitness Calculators */}
            <Route path="fitness/bmi-calculator" element={<BMICalculatorPage />} />
            <Route path="fitness/calorie-calculator" element={<CalorieCalculatorPage />} />
            <Route path="fitness/body-fat-calculator" element={<BodyFatCalculatorPage />} />
            <Route path="fitness/macro-calculator" element={<MacroCalculatorPage />} />
            <Route path="fitness/ideal-weight-calculator" element={<IdealWeightCalculatorPage />} />
            
            {/* Legacy routes for backward compatibility */}
            <Route path="calculators/mortgage" element={<MortgageCalculatorPage />} />
            <Route path="calculators/investment" element={<InvestmentCalculatorPage />} />
            <Route path="calculators/retirement" element={<RetirementCalculatorPage />} />
            <Route path="calculators/scientific" element={<ScientificCalculatorPage />} />
            <Route path="calculators/percentage" element={<PercentageCalculatorPage />} />
            <Route path="calculators/bmi" element={<BMICalculatorPage />} />
            <Route path="calculators/unit-conversion" element={<UnitConversionCalculatorPage />} />
            <Route path="calculators/calorie" element={<CalorieCalculatorPage />} />
            <Route path="calculators/amortization" element={<AmortizationCalculatorPage />} />
            <Route path="calculators/interest" element={<InterestCalculatorPage />} />
            
            {/* Error Routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BookmarkProvider>
    </ThemeProvider>
  );
}

export default App;
